<template>
  <v-container fluid class="pa-4">
    <div class="d-flex align-center justify-space-between mb-1 flex-wrap" style="gap: 12px">
      <div>
        <span class="text-h5 font-weight-bold">
          <v-icon left color="primary">mdi-timer-outline</v-icon>
          Approve / Decline Overtime
        </span>
        <p class="text-body-2 grey--text mb-0 mt-1">
          Review and act on pending overtime requests from your team
        </p>
      </div>
      <v-chip outlined>
        <span class="font-weight-bold">{{ pendingOvertime.length }} pending</span>
      </v-chip>
    </div>

    <template v-if="isLoadingPending">
      <v-row class="mt-2">
        <v-col v-for="n in 3" :key="n" cols="12" md="6" lg="4">
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
    </template>
    <div v-else-if="pendingOvertime.length === 0" class="text-center py-12">
      <v-icon size="64" color="grey lighten-1">mdi-timer-sand-empty</v-icon>
      <p class="text-h6 grey--text mt-4">No pending overtime requests</p>
    </div>

    <v-row v-else class="mt-2">
      <v-col v-for="upcoming in pendingOvertime" :key="upcoming.id" cols="12" md="6" lg="4">
        <v-card outlined class="pa-3 overtime-request-card">
          <div class="d-flex align-center mb-3">
            <v-avatar size="36" color="primary" class="mr-3">
              <span class="white--text text-caption font-weight-bold">
                {{ getInitials(upcoming.user?.name) }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-bold">{{ upcoming.user?.name || "Unknown user" }}</div>
              <div class="text-caption grey--text">{{ upcoming.user?.role?.name || "" }}</div>
            </div>
            <v-spacer />
            <v-chip x-small color="orange" text-color="white">Pending</v-chip>
          </div>

          <div class="d-flex align-center mb-3" style="gap: 8px">
            <v-chip small label>
              <v-icon x-small left>mdi-calendar</v-icon>
              {{ $date(upcoming.planned_data).format("MMM D, YYYY") }}
            </v-chip>
            <v-chip small label>
              <v-icon x-small left>mdi-clock-outline</v-icon>
              {{ upcoming.overtime }}
            </v-chip>
          </div>

          <v-divider class="mb-3" />

          <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">
            {{ upcoming.overtime_data.length }}
            task{{ upcoming.overtime_data.length !== 1 ? "s" : "" }}
          </div>
          <div
            v-for="(item, itemIndex) in upcoming.overtime_data"
            :key="itemIndex"
            class="task-line mb-2"
          >
            <div class="d-flex align-center flex-wrap">
              <span class="font-weight-medium text-body-2">
                {{ item.task?.name || "Task has no name" }}
              </span>
              <v-chip x-small outlined class="ml-2" v-if="item.task?.company?.name">
                {{ item.task.company.name }}
              </v-chip>
            </div>
            <div v-if="item.note_per_task" class="text-caption grey--text font-italic">
              {{ item.note_per_task }}
            </div>
          </div>

          <v-textarea v-model="decisionNotes[upcoming.id]" outlined dense rows="2" hide-details
            placeholder="Add a note for the employee (optional)" class="mt-2"></v-textarea>

          <v-divider class="my-3" />

          <div class="d-flex justify-end" style="gap: 8px">
            <v-btn
              small
              outlined
              color="error"
              class="text-none"
              :loading="updatingId === upcoming.id"
              @click="declineRequest(upcoming)"
            >
              Decline
            </v-btn>
            <v-btn
              small
              color="success"
              dark
              class="text-none"
              :loading="updatingId === upcoming.id"
              @click="approveRequest(upcoming)"
            >
              Approve
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <div class="d-flex align-center mt-6 mb-1">
      <span class="text-h6 font-weight-bold">
        <v-icon left color="primary">mdi-history</v-icon>
        History
      </span>
    </div>
    <template v-if="isLoadingHistory">
      <v-card outlined>
        <v-skeleton-loader v-for="n in 3" :key="n" type="list-item-two-line" class="pa-3" />
      </v-card>
    </template>
    <div v-else-if="historyOvertime.length === 0" class="text-center py-8">
      <p class="text-body-2 grey--text mb-0">No decisions made yet</p>
    </div>
    <v-card v-else outlined>
      <div v-for="item in historyOvertime" :key="item.id" class="request-row">
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
          <div>
            <span class="font-weight-bold text-body-2">{{ item.user?.name }}</span>
            <span class="text-caption grey--text ml-2">
              {{ item.overtime_data.length }} task{{ item.overtime_data.length !== 1 ? "s" : "" }}
            </span>
          </div>
          <v-chip x-small label :color="getOvertimeStatusColor(item.overtime_status?.name)" text-color="white">
            <v-icon x-small left>{{ getOvertimeStatusIcon(item.overtime_status?.name) }}</v-icon>
            {{ item.overtime_status?.name }}
          </v-chip>
        </div>
        <div class="text-caption grey--text mt-1">
          {{ $date(item.planned_data).format("MMM D, YYYY") }} &middot; {{ item.overtime }}
        </div>
        <div v-if="item.approver_note" class="text-caption grey--text font-italic mt-1">
          Your note: {{ item.approver_note }}
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
/**vuex */
import { mapGetters } from "vuex";
/**shared helpers */
import { getInitials, getOvertimeStatusColor, getOvertimeStatusIcon } from "./leaveOvertimeUtils";

export default {
  inject: ["refreshApprovalCounts"],
  data: () => {
    return {
      overview: [],
      statuses: [],
      isLoadingPending: true,
      isLoadingHistory: true,
      updatingId: null,
      decisionNotes: {},
    };
  },
  async mounted() {
    await Promise.all([this.getOvertimeOverview(), this.overtimeStatus()]);
  },
  computed: {
    ...mapGetters({
      user: "auth/get_user",
    }),
    pendingStatusId() {
      const found = this.statuses.find((s) => (s.name || "").toLowerCase().includes("pending"));
      return found ? found.id : 1;
    },
    approvedStatusId() {
      const found = this.statuses.find((s) => (s.name || "").toLowerCase().includes("approv"));
      return found ? found.id : 2;
    },
    declinedStatusId() {
      const found = this.statuses.find((s) => (s.name || "").toLowerCase().includes("declin"));
      return found ? found.id : 3;
    },
    pendingOvertime() {
      return this.overview
        .filter((item) => item.approver_id === this.user.id && item.overtime_status?.id === this.pendingStatusId)
        .sort((a, b) => b.id - a.id);
    },
    historyOvertime() {
      return this.overview
        .filter((item) => item.approver_id === this.user.id && item.overtime_status?.id !== this.pendingStatusId)
        .sort((a, b) => b.id - a.id);
    },
  },
  methods: {
    getInitials,
    getOvertimeStatusColor,
    getOvertimeStatusIcon,

    async approveRequest(overtimeData) {
      await this.updateStatus(overtimeData, this.approvedStatusId);
    },

    async declineRequest(overtimeData) {
      await this.updateStatus(overtimeData, this.declinedStatusId);
    },

    async updateStatus(overtimeData, statusId) {
      this.updatingId = overtimeData.id;
      try {
        await this.updateOvertimeRequest(overtimeData.id, {
          overtime_status: statusId,
          approver_note: this.decisionNotes[overtimeData.id] || null,
        });
        this.$toast.success("Overtime request updated");
        this.$delete(this.decisionNotes, overtimeData.id);
      } catch (error) {
        console.log(
          "error on updating overtime status - component level",
          error
        );
        this.$toast.error("Error occurred!");
      } finally {
        this.updatingId = null;
      }
    },

    /**
    get all overtime requests across all users, used to derive this approver's
    pending queue and decision history
    @params
    none
    */
    async getOvertimeOverview() {
      this.isLoadingPending = true;
      this.isLoadingHistory = true;
      try {
        const { data } = await this.$axios.get("api/overtime/overview");
        this.overview = data;
        data.forEach((item) => {
          if (!(item.id in this.decisionNotes)) {
            this.$set(this.decisionNotes, item.id, "");
          }
        });
      } catch (error) {
        console.log(
          "error on fetching overtime overview - service layer",
          error
        );
      } finally {
        this.isLoadingPending = false;
        this.isLoadingHistory = false;
      }
    },

    /**
    get overtime status
    @params
    none
    */
    async overtimeStatus() {
      try {
        const { data } = await this.$axios.get("api/overtime/status");
        this.statuses = data;
      } catch (error) {
        console.log("error on fetching overtime status - service layer", error);
      }
    },

    /**
    update overtime request of get_user
    @params
    record_id?: number,
    item?: object
    */
    async updateOvertimeRequest(record_id, item) {
      try {
        await this.$axios.patch(`api/overtime/${record_id}`, item);
        await this.getOvertimeOverview();
        if (typeof this.refreshApprovalCounts === "function") {
          this.refreshApprovalCounts();
        }
      } catch (error) {
        console.log("error on updating overtime status - service layer", error);
        throw error;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.overtime-request-card {
  height: 100%;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  }
}

.task-line {
  &:last-child {
    margin-bottom: 0 !important;
  }
}

.request-row {
  padding: 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  &:last-child {
    border-bottom: none;
  }
}
</style>
